import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Collapse,
} from '@mui/material';
import { Link as RouterLink, useSearchParams, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gtmQuizCompleted } from '../../utils/gtm';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircle from '@mui/icons-material/CheckCircle';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import EditOutlined from '@mui/icons-material/EditOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import VerifiedOutlined from '@mui/icons-material/VerifiedOutlined';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import ReplayOutlined from '@mui/icons-material/ReplayOutlined';
import SmartProvinceSelector from '../shared/SmartProvinceSelector';

/* ── Province labels for summary ── */
const PROVINCE_LABELS_STATIC = {
  ON: 'Ontario', QC: 'Quebec', BC: 'British Columbia', AB: 'Alberta',
  MB: 'Manitoba', SK: 'Saskatchewan', NS: 'Nova Scotia', NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador', PE: 'Prince Edward Island',
  NT: 'Northwest Territories', YT: 'Yukon', NU: 'Nunavut',
};

/**
 * Pure recommendation logic — returns { primary, secondary } objects.
 * primary includes: headline (curated label), title, description, bullets[]
 * secondary includes: title, description
 */
export const getRecommendation = (province, status, priority, t) => {
  if (status === 'newcomer') {
    if (province === 'QC') {
      return {
        primary: {
          headline: t('banking.picker.rec.newcomerQC.headline'),
          title: t('banking.picker.rec.newcomerQC.primaryTitle'),
          description: t('banking.picker.rec.newcomerQC.primaryDesc'),
          bullets: [
            t('banking.picker.rec.newcomerQC.bullet1'),
            t('banking.picker.rec.newcomerQC.bullet2'),
            t('banking.picker.rec.newcomerQC.bullet3'),
          ],
        },
        secondary: {
          title: t('banking.picker.rec.newcomerQC.secondaryTitle'),
          description: t('banking.picker.rec.newcomerQC.secondaryDesc'),
        },
      };
    }
    if (priority === 'card_account') {
      return {
        primary: {
          headline: t('banking.picker.rec.newcomerCardAccount.headline'),
          title: t('banking.picker.rec.newcomerCardAccount.primaryTitle'),
          description: t('banking.picker.rec.newcomerCardAccount.primaryDesc'),
          bullets: [
            t('banking.picker.rec.newcomerCardAccount.bullet1'),
            t('banking.picker.rec.newcomerCardAccount.bullet2'),
            t('banking.picker.rec.newcomerCardAccount.bullet3'),
          ],
        },
        secondary: {
          title: t('banking.picker.rec.newcomerCardAccount.secondaryTitle'),
          description: t('banking.picker.rec.newcomerCardAccount.secondaryDesc'),
        },
      };
    }
    if (priority === 'cash_bonus') {
      return {
        primary: {
          headline: t('banking.picker.rec.newcomerCashBonus.headline'),
          title: t('banking.picker.rec.newcomerCashBonus.primaryTitle'),
          description: t('banking.picker.rec.newcomerCashBonus.primaryDesc'),
          bullets: [
            t('banking.picker.rec.newcomerCashBonus.bullet1'),
            t('banking.picker.rec.newcomerCashBonus.bullet2'),
            t('banking.picker.rec.newcomerCashBonus.bullet3'),
          ],
        },
        secondary: {
          title: t('banking.picker.rec.newcomerCashBonus.secondaryTitle'),
          description: t('banking.picker.rec.newcomerCashBonus.secondaryDesc'),
        },
      };
    }
    return {
      primary: {
        headline: t('banking.picker.rec.newcomerDefault.headline'),
        title: t('banking.picker.rec.newcomerDefault.primaryTitle'),
        description: t('banking.picker.rec.newcomerDefault.primaryDesc'),
        bullets: [
          t('banking.picker.rec.newcomerDefault.bullet1'),
          t('banking.picker.rec.newcomerDefault.bullet2'),
          t('banking.picker.rec.newcomerDefault.bullet3'),
        ],
      },
      secondary: {
        title: t('banking.picker.rec.newcomerDefault.secondaryTitle'),
        description: t('banking.picker.rec.newcomerDefault.secondaryDesc'),
      },
    };
  }

  // Established
  if (priority === 'no_fee') {
    return {
      primary: {
        headline: t('banking.picker.rec.establishedNoFee.headline'),
        title: t('banking.picker.rec.establishedNoFee.primaryTitle'),
        description: t('banking.picker.rec.establishedNoFee.primaryDesc'),
        bullets: [
          t('banking.picker.rec.establishedNoFee.bullet1'),
          t('banking.picker.rec.establishedNoFee.bullet2'),
          t('banking.picker.rec.establishedNoFee.bullet3'),
        ],
      },
      secondary: {
        title: t('banking.picker.rec.establishedNoFee.secondaryTitle'),
        description: t('banking.picker.rec.establishedNoFee.secondaryDesc'),
      },
    };
  }
  if (priority === 'high_interest') {
    return {
      primary: {
        headline: t('banking.picker.rec.establishedHighInterest.headline'),
        title: t('banking.picker.rec.establishedHighInterest.primaryTitle'),
        description: t('banking.picker.rec.establishedHighInterest.primaryDesc'),
        bullets: [
          t('banking.picker.rec.establishedHighInterest.bullet1'),
          t('banking.picker.rec.establishedHighInterest.bullet2'),
          t('banking.picker.rec.establishedHighInterest.bullet3'),
        ],
      },
      secondary: {
        title: t('banking.picker.rec.establishedHighInterest.secondaryTitle'),
        description: t('banking.picker.rec.establishedHighInterest.secondaryDesc'),
      },
    };
  }
  if (priority === 'cash_bonus') {
    return {
      primary: {
        headline: t('banking.picker.rec.establishedCashBonus.headline'),
        title: t('banking.picker.rec.establishedCashBonus.primaryTitle'),
        description: t('banking.picker.rec.establishedCashBonus.primaryDesc'),
        bullets: [
          t('banking.picker.rec.establishedCashBonus.bullet1'),
          t('banking.picker.rec.establishedCashBonus.bullet2'),
          t('banking.picker.rec.establishedCashBonus.bullet3'),
        ],
      },
      secondary: {
        title: t('banking.picker.rec.establishedCashBonus.secondaryTitle'),
        description: t('banking.picker.rec.establishedCashBonus.secondaryDesc'),
      },
    };
  }
  // card_account
  return {
    primary: {
      headline: t('banking.picker.rec.establishedCardAccount.headline'),
      title: t('banking.picker.rec.establishedCardAccount.primaryTitle'),
      description: t('banking.picker.rec.establishedCardAccount.primaryDesc'),
      bullets: [
        t('banking.picker.rec.establishedCardAccount.bullet1'),
        t('banking.picker.rec.establishedCardAccount.bullet2'),
        t('banking.picker.rec.establishedCardAccount.bullet3'),
      ],
    },
    secondary: {
      title: t('banking.picker.rec.establishedCardAccount.secondaryTitle'),
      description: t('banking.picker.rec.establishedCardAccount.secondaryDesc'),
    },
  };
};

/* ── Trust Badge ── */
const TrustBadge = ({ icon, label }) => (
  <Box
    sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      px: 1.5, py: 0.5, borderRadius: '20px',
      bgcolor: 'rgba(255,255,255,0.7)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}
  >
    {icon}
    <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#5F6B7A' }}>
      {label}
    </Typography>
  </Box>
);

const VALID_PROVINCES = new Set([...Object.keys(PROVINCE_LABELS_STATIC), 'UNKNOWN']);
const VALID_STATUSES = new Set(['newcomer', 'established']);
const VALID_PRIORITIES = new Set(['no_fee', 'high_interest', 'cash_bonus', 'card_account']);

const AccountPicker60s = () => {
  const { t } = useTranslation();
  const { lng } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const PROVINCE_LABELS = useMemo(() => ({
    ...PROVINCE_LABELS_STATIC,
    UNKNOWN: t('banking.picker.provinceUnknown'),
  }), [t]);

  const STATUS_OPTIONS = useMemo(() => [
    { value: 'newcomer', label: t('banking.picker.statusNewcomer'), emoji: '\u{1F331}' },
    { value: 'established', label: t('banking.picker.statusEstablished'), emoji: '\u{1F3E1}' },
  ], [t]);

  const PRIORITY_OPTIONS = useMemo(() => [
    { value: 'no_fee', label: t('banking.picker.priorityNoFee'), emoji: '\u{1F4B8}' },
    { value: 'high_interest', label: t('banking.picker.priorityHighInterest'), emoji: '\u{1F4C8}' },
    { value: 'cash_bonus', label: t('banking.picker.priorityCashBonus'), emoji: '\u{1F381}' },
    { value: 'card_account', label: t('banking.picker.priorityCardAccount'), emoji: '\u{1F4B3}' },
  ], [t]);

  const QUESTIONS = useMemo(() => [
    {
      id: 'province',
      stepNum: 1,
      title: t('banking.picker.questionProvince'),
      autoAdvance: true,
      isComplete: (s) => !!s.province,
      summary: (s) => PROVINCE_LABELS[s.province] || '',
    },
    {
      id: 'status',
      stepNum: 2,
      title: t('banking.picker.questionStatus'),
      autoAdvance: true,
      isComplete: (s) => !!s.status,
      summary: (s) => {
        const opt = STATUS_OPTIONS.find((o) => o.value === s.status);
        return opt ? `${opt.emoji} ${opt.label}` : '';
      },
    },
    {
      id: 'priority',
      stepNum: 3,
      title: t('banking.picker.questionPriority'),
      autoAdvance: true,
      isComplete: (s) => !!s.priority,
      summary: (s) => {
        const opt = PRIORITY_OPTIONS.find((o) => o.value === s.priority);
        return opt ? `${opt.emoji} ${opt.label}` : '';
      },
    },
  ], [t, PROVINCE_LABELS, STATUS_OPTIONS, PRIORITY_OPTIONS]);

  // Hydrate from URL params on mount
  const initProvince = VALID_PROVINCES.has(searchParams.get('prov')) ? searchParams.get('prov') : null;
  const initStatus = VALID_STATUSES.has(searchParams.get('status')) ? searchParams.get('status') : null;
  const initPriority = VALID_PRIORITIES.has(searchParams.get('priority')) ? searchParams.get('priority') : null;
  const initStep = initPriority ? 2 : initStatus ? 2 : initProvince ? 1 : 0;

  const [province, setProvince] = useState(initProvince);
  const [status, setStatus] = useState(initStatus);
  const [priority, setPriority] = useState(initPriority);
  const [activeStep, setActiveStep] = useState(initStep);
  const [editing, setEditing] = useState(false); // whether quiz is expanded when result shows
  const autoAdvanceRef = useRef(null);
  const skipAutoAdvanceRef = useRef(false);
  const resultRef = useRef(null);

  const state = { province, status, priority };

  // Sync answers to URL params
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    let changed = false;
    const sync = (key, val) => {
      if (val) { if (params.get(key) !== val) { params.set(key, val); changed = true; } }
      else { if (params.has(key)) { params.delete(key); changed = true; } }
    };
    sync('prov', province);
    sync('status', status);
    sync('priority', priority);
    if (changed) setSearchParams(params, { replace: true });
  }, [province, status, priority]); // eslint-disable-line react-hooks/exhaustive-deps

  const showResult = province && status && priority;
  const result = showResult ? getRecommendation(province, status, priority, t) : null;
  const quizFiredRef = useRef(false);
  useEffect(() => {
    if (showResult && !quizFiredRef.current) {
      quizFiredRef.current = true;
      gtmQuizCompleted({ quiz: 'account_picker', segment: `${province}_${status}_${priority}`, locale: lng });
    }
  }, [showResult, province, status, priority, lng]);

  // Scroll to result when it appears
  useEffect(() => {
    if (showResult && resultRef.current) {
      setEditing(false); // collapse quiz when result first appears
      setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);
    }
  }, [showResult]);

  // Compute visible count (unlocked questions)
  const visibleCount = (() => {
    let count = 1;
    for (let i = 0; i < QUESTIONS.length - 1; i++) {
      if (QUESTIONS[i].isComplete(state)) count = i + 2;
      else break;
    }
    return Math.min(count, QUESTIONS.length);
  })();

  // Auto-advance effect
  useEffect(() => {
    if (autoAdvanceRef.current) clearTimeout(autoAdvanceRef.current);

    const q = QUESTIONS[activeStep];
    if (!q) return;

    if (q.autoAdvance && q.isComplete(state)) {
      if (skipAutoAdvanceRef.current) {
        skipAutoAdvanceRef.current = false;
        return;
      }
      autoAdvanceRef.current = setTimeout(() => {
        if (activeStep < QUESTIONS.length - 1) {
          setActiveStep(activeStep + 1);
        }
      }, 300);
    }

    return () => { if (autoAdvanceRef.current) clearTimeout(autoAdvanceRef.current); };
  }, [province, status, priority, activeStep]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleProvinceSelect = (value) => setProvince(value);
  const handleStatusSelect = (value) => setStatus(value);
  const handlePrioritySelect = (value) => setPriority(value);

  const handleReset = () => {
    setProvince(null);
    setStatus(null);
    setPriority(null);
    setActiveStep(0);
    setEditing(false);
    setSearchParams({}, { replace: true });
  };

  const handleEditStep = (idx) => {
    skipAutoAdvanceRef.current = true;
    setActiveStep(idx);
  };

  const handleEditAnswers = () => {
    setEditing(true);
    setActiveStep(2); // open last step by default so user can change priority
  };

  /* ── Render question content ── */
  const renderQuestionContent = (q) => {
    if (q.id === 'province') {
      return (
        <SmartProvinceSelector
          value={province}
          onSelect={handleProvinceSelect}
          showUnsure
        />
      );
    }

    if (q.id === 'status') {
      return (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
          {STATUS_OPTIONS.map((opt) => {
            const selected = status === opt.value;
            return (
              <Box
                key={opt.value}
                onClick={() => handleStatusSelect(opt.value)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleStatusSelect(opt.value); } }}
                sx={{
                  p: 2, borderRadius: '12px', cursor: 'pointer', textAlign: 'center',
                  border: selected ? '1.5px solid #1E3A5F' : '1px solid transparent',
                  bgcolor: selected ? '#F7F9FC' : '#FFFFFF',
                  boxShadow: selected ? '0 2px 8px rgba(30,58,95,0.14)' : '0 1px 4px rgba(0,0,0,0.06)',
                  transition: 'all 0.2s ease',
                  '&:hover': { boxShadow: '0 3px 10px rgba(30,58,95,0.10)', bgcolor: '#F7F9FC' },
                  '&:focus-visible': { outline: '2px solid #1E3A5F', outlineOffset: 2 },
                }}
              >
                <Typography sx={{ fontSize: '20px', mb: 0.5 }}>{opt.emoji}</Typography>
                <Typography sx={{ fontSize: '13px', fontWeight: selected ? 700 : 500, color: '#1A1A1A' }}>
                  {opt.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      );
    }

    if (q.id === 'priority') {
      return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 1.5 }}>
          {PRIORITY_OPTIONS.map((opt) => {
            const selected = priority === opt.value;
            return (
              <Box
                key={opt.value}
                onClick={() => handlePrioritySelect(opt.value)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handlePrioritySelect(opt.value); } }}
                sx={{
                  p: 2, borderRadius: '12px', cursor: 'pointer', textAlign: 'center',
                  border: selected ? '1.5px solid #1E3A5F' : '1px solid transparent',
                  bgcolor: selected ? '#F7F9FC' : '#FFFFFF',
                  boxShadow: selected ? '0 2px 8px rgba(30,58,95,0.14)' : '0 1px 4px rgba(0,0,0,0.06)',
                  transition: 'all 0.2s ease',
                  '&:hover': { boxShadow: '0 3px 10px rgba(30,58,95,0.10)', bgcolor: '#F7F9FC' },
                  '&:focus-visible': { outline: '2px solid #1E3A5F', outlineOffset: 2 },
                }}
              >
                <Typography sx={{ fontSize: '20px', mb: 0.5 }}>{opt.emoji}</Typography>
                <Typography sx={{ fontSize: '13px', fontWeight: selected ? 700 : 500, color: '#1A1A1A' }}>
                  {opt.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      );
    }

    return null;
  };

  return (
    <Box
      role="region"
      aria-label={t('banking.picker.title')}
      sx={{
        bgcolor: '#FFFFFF',
        border: '1px solid #EEF0F3',
        borderRadius: '16px',
        boxShadow: '0 6px 18px rgba(0,0,0,0.05)',
        p: { xs: 3, md: 4 },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <TimerOutlinedIcon sx={{ fontSize: '1.1rem', color: '#1E3A5F' }} />
        <Typography
          sx={{ fontSize: '12px', fontWeight: 600, color: '#5F6B7A', textTransform: 'uppercase', letterSpacing: '0.08em' }}
        >
          {t('banking.picker.badge60s')}
        </Typography>
      </Box>
      <Typography variant="h6" component="h3" sx={{ fontWeight: 700, color: '#1A1A1A', mb: 0.5 }}>
        {t('banking.picker.title')}
      </Typography>
      <Typography sx={{ fontSize: '14px', color: '#5F6B7A', mb: 2 }}>
        {t('banking.picker.subtitle')}
      </Typography>

      {/* Trust badges strip */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        <TrustBadge icon={<TimerOutlinedIcon sx={{ fontSize: 14, color: '#2E7D32' }} />} label={t('banking.picker.badge60s')} />
        <TrustBadge icon={<VerifiedOutlined sx={{ fontSize: 14, color: '#1E3A5F' }} />} label={t('banking.picker.badgeNoSignup')} />
        <TrustBadge icon={<TuneOutlined sx={{ fontSize: 14, color: '#F9A825' }} />} label={t('banking.picker.badgePersonalized')} />
      </Box>

      {/* ── QUIZ QUESTIONS (collapse when result shows, expand on edit) ── */}
      {showResult && !editing ? (
        /* Summary bar — collapsed state */
        <Box
          sx={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { xs: 0.75, sm: 1.5 },
            px: 2.5, py: 1.5, mb: 3,
            borderRadius: '10px', bgcolor: '#F7F9FC',
            border: '1px solid #EEF0F3',
          }}
        >
          {QUESTIONS.map((q, idx) => (
            <Box key={q.id} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <CheckCircle sx={{ fontSize: '0.85rem', color: '#2E7D32' }} />
              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#1A1A1A' }}>
                {q.summary(state)}
              </Typography>
              {idx < QUESTIONS.length - 1 && (
                <Typography component="span" sx={{ color: '#C4C9D2', ml: 0.5, fontSize: '13px', userSelect: 'none' }}>·</Typography>
              )}
            </Box>
          ))}
          <Button
            size="small"
            onClick={handleEditAnswers}
            startIcon={<EditOutlined sx={{ fontSize: '14px !important' }} />}
            sx={{
              ml: 'auto', textTransform: 'none', fontSize: '12px', fontWeight: 600,
              color: '#5F6B7A', px: 1.5, minWidth: 'auto',
              '&:hover': { color: '#1E3A5F', bgcolor: 'transparent' },
            }}
          >
            {t('banking.picker.editAnswers')}
          </Button>
        </Box>
      ) : (
        /* Full quiz — active or editing state */
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: showResult ? 3 : 0 }}>
          {QUESTIONS.map((q, idx) => {
            const isVisible = idx < visibleCount;
            const isFocused = activeStep === idx;
            const isComplete = q.isComplete(state);
            const isLocked = !isVisible;

            if (isLocked) {
              return (
                <Box key={q.id} sx={{ p: 2, borderRadius: '12px', bgcolor: '#FAFBFD', opacity: 0.35 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LockOutlined sx={{ fontSize: 16, color: '#C4C9D2' }} />
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#C4C9D2' }}>
                      {q.stepNum}. {q.title}
                    </Typography>
                  </Box>
                </Box>
              );
            }

            if (!isFocused && isComplete) {
              return (
                <Box
                  key={q.id}
                  onClick={() => handleEditStep(idx)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleEditStep(idx); } }}
                  sx={{
                    p: 2, borderRadius: '12px', border: 'none', bgcolor: '#FAFBFD',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.04)', cursor: 'pointer', transition: 'all 0.2s ease',
                    '&:hover': { bgcolor: '#F7F9FC', boxShadow: '0 3px 12px rgba(30,58,95,0.08)' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ fontSize: 18, color: '#2E7D32' }} />
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1A1A1A', flex: 1 }}>
                      {q.summary(state)}
                    </Typography>
                    <EditOutlined sx={{ fontSize: 16, color: '#5F6B7A' }} />
                  </Box>
                </Box>
              );
            }

            return (
              <Box
                key={q.id}
                sx={{
                  p: { xs: 2, md: 3 }, borderRadius: '14px', border: 'none', bgcolor: '#FFFFFF',
                  borderLeft: '3px solid #1E3A5F',
                  boxShadow: '0 8px 28px rgba(30,58,95,0.10), -2px 0 8px rgba(30,58,95,0.04)',
                  animation: 'fadeIn 0.3s ease',
                  '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
                }}
              >
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1A1A1A', mb: 2 }}>
                  {q.stepNum}. {q.title}
                </Typography>
                {renderQuestionContent(q)}
              </Box>
            );
          })}

          {/* Collapse editor button when editing with result visible */}
          {showResult && editing && (
            <Button
              size="small"
              onClick={() => setEditing(false)}
              sx={{
                alignSelf: 'flex-end', textTransform: 'none', fontSize: '13px',
                fontWeight: 600, color: '#1E3A5F',
                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
              }}
            >
              {t('banking.picker.collapseQuiz')}
            </Button>
          )}
        </Box>
      )}

      {/* ── RESULT ── */}
      {showResult && result && (
        <Box ref={resultRef} role="status" aria-live="polite">

          {/* ── Primary recommendation card ── */}
          <Box
            sx={{
              position: 'relative', mb: 2,
              borderRadius: '16px',
              border: '1.5px solid #1E3A5F',
              bgcolor: '#FFFFFF',
              boxShadow: '0 8px 32px rgba(30,58,95,0.10)',
              overflow: 'visible',
              '@keyframes fadeInCard': {
                from: { opacity: 0, transform: 'translateY(8px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
              animation: 'fadeInCard 0.3s ease-out 0.1s both',
            }}
          >
            {/* Floating badge */}
            <Chip
              label={t('banking.picker.bestOption')}
              size="small"
              sx={{
                position: 'absolute', top: -12, left: 20,
                bgcolor: '#1E3A5F', color: '#FFFFFF',
                fontWeight: 700, fontSize: '0.75rem', height: 24,
                boxShadow: '0 2px 8px rgba(30,58,95,0.25)',
              }}
            />

            <Box sx={{ p: { xs: 2.5, md: 3.5 }, pt: { xs: 3.5, md: 4 } }}>
              {/* Curated headline */}
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#2E7D32', mb: 0.5 }}>
                {result.primary.headline}
              </Typography>
              {/* Strong title */}
              <Typography sx={{ fontWeight: 700, color: '#1A1A1A', fontSize: { xs: '18px', md: '20px' }, mb: 1, lineHeight: 1.3 }}>
                {result.primary.title}
              </Typography>
              {/* Description */}
              <Typography sx={{ fontSize: '14px', color: '#5F6B7A', lineHeight: 1.6, mb: 2 }}>
                {result.primary.description}
              </Typography>
              {/* Bullet points */}
              {result.primary.bullets?.length > 0 && (
                <Box component="ul" sx={{ m: 0, pl: 0, listStyle: 'none', mb: 2.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {result.primary.bullets.map((bullet, i) => (
                    <Box component="li" key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <CheckCircleOutline sx={{ fontSize: 16, color: '#2E7D32', mt: '2px', flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '13px', color: '#1A1A1A', lineHeight: 1.5 }}>
                        {bullet}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              {/* CTAs */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                <Button
                  variant="contained"
                  component={RouterLink}
                  to={`/${lng}/comparar-cuentas`}
                  endIcon={<ArrowForwardIcon sx={{ fontSize: '16px !important' }} />}
                  onClick={() => {
                    if (window.plausible) window.plausible('Banking Quiz CTA', { props: { type: 'compare', province, status, priority } });
                  }}
                  sx={{
                    textTransform: 'none', fontWeight: 700, fontSize: '14px',
                    borderRadius: '10px', px: 3, py: { xs: 1.5, sm: 1 },
                    minHeight: 44,
                    bgcolor: '#1E3A5F',
                    '&:hover': { bgcolor: '#1A3152', transform: 'translateY(-2px)', boxShadow: '0 4px 16px rgba(30,58,95,0.25)' },
                    '&:focus-visible': { outline: '2px solid #1E3A5F', outlineOffset: 2 },
                  }}
                >
                  {t('banking.picker.ctaCompareAccounts')}
                </Button>
              </Box>
            </Box>
          </Box>

          {/* ── Secondary recommendation card ── */}
          <Box
            sx={{
              mb: 2.5,
              borderRadius: '10px',
              border: '1px solid #EEF0F3',
              bgcolor: '#FAFBFD',
              animation: 'fadeInCard 0.3s ease-out 0.2s both',
            }}
          >
            <Box sx={{ px: 2.5, py: 2 }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>
                {t('banking.picker.alsoRecommend')}
              </Typography>
              <Typography sx={{ fontWeight: 600, color: '#1A1A1A', fontSize: '14px', mb: 0.5, lineHeight: 1.3 }}>
                {result.secondary.title}
              </Typography>
              <Typography sx={{ fontSize: '13px', color: '#5F6B7A', lineHeight: 1.5, mb: 1.5 }}>
                {result.secondary.description}
              </Typography>
              <Button
                variant="text"
                size="small"
                component={RouterLink}
                to={`/${lng}/comparar-cuentas`}
                sx={{
                  textTransform: 'none', fontWeight: 600, fontSize: '13px',
                  color: '#1E3A5F', p: 0, minWidth: 'auto',
                  '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                }}
              >
                {t('banking.picker.ctaSavingsAccounts')}
              </Button>
            </Box>
          </Box>

          {/* ── Footer: disclosure + reset (same row) ── */}
          <Box
            sx={{
              display: 'flex', flexWrap: 'wrap', alignItems: 'center',
              justifyContent: 'space-between', gap: 1,
              animation: 'fadeInCard 0.3s ease-out 0.25s both',
            }}
          >
            <Typography sx={{ fontSize: '12px', color: '#9CA3AF', lineHeight: 1.5, flex: 1, minWidth: { xs: 0, sm: 200 } }}>
              {t('banking.picker.disclosure')}
            </Typography>
            <Button
              variant="text"
              size="small"
              onClick={handleReset}
              startIcon={<ReplayOutlined sx={{ fontSize: '13px !important' }} />}
              sx={{
                textTransform: 'none', fontWeight: 600, color: '#9CA3AF',
                fontSize: '11px', p: 0, minWidth: 'auto',
                '&:hover': { color: '#5F6B7A', bgcolor: 'transparent' },
              }}
            >
              {t('banking.picker.startOver')}
            </Button>
          </Box>

        </Box>
      )}
    </Box>
  );
};

export default AccountPicker60s;
